import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  MagnifyingGlass as Search,
  Envelope as Mail,
  Phone,
  Briefcase,
  X,
  CheckCircle,
  DownloadSimple as Download,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { RowActions } from '@/components/shared/RowActions';
import { TablePagination } from '@/components/shared/TablePagination';
import { useContactsStore, type Contact } from '@/lib/contactsStore';
import { searchContacts } from '@/lib/contactsApi';
import { useServerSearch } from '@/lib/useServerSearch';
import { useAuthStore } from '@/features/auth/store';
import { canMutateContacts } from '@/lib/pupilUiBoundary';
import { exportToCsv } from '@/lib/exportUtils';
import { PhoneInput } from '@/components/ui/phone-input';
import { sanitizeName, isValidName, isValidEmail, isValidPhone, NAME_ERROR, EMAIL_ERROR, PHONE_ERROR } from '@/lib/validators';
import { ScrollLock } from '@/components/shared/ScrollLock';

/* ─── Constants ─── */

const KIND_LABEL: Record<string, string> = {
  lay: 'Lay client', defendant: 'Defendant client', solicitor: 'Solicitor', expert: 'Expert', witness: 'Witness', company: 'Company',
};

const TYPE_OPTIONS = ['lay', 'defendant', 'solicitor', 'expert', 'witness', 'company'] as const;

function formatDate(ts: number): string {
  if (!ts || ts < 1000) return '—';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ─── Main ─── */

export default function MyClientsPage() {
  const navigate = useNavigate();
  const contacts = useContactsStore((s) => s.contacts);
  const fetchContacts = useContactsStore((s) => s.fetchContacts);
  const deleteContact = useContactsStore((s) => s.deleteContact);
  const updateContact = useContactsStore((s) => s.updateContact);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const permissions = useAuthStore((s) => s.permissions);
  // Contacts are READ-ONLY for a pupil: contact.create / contact.update.any both 403.
  // (Delete already checks its own RBAC key below.)
  const canMutate = canMutateContacts(useAuthStore((s) => s.user?.memberType));
  const canDelete = permissions.includes('contact.delete');

  const [search, setSearch] = useState('');
  const [editClient, setEditClient] = useState<Contact | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const q = search.toLowerCase();
  // Server-side contact search (name/org/email/phone) finds contacts beyond the
  // client-loaded store; the in-memory filter is the idle path only. Canonical hook (H3).
  const { isSearching, results: serverContacts } = useServerSearch<Contact>(search, {
    key: ['my-clients-search'],
    fetcher: (q) => searchContacts(q, undefined, 50) as unknown as Promise<Contact[]>,
  });

  const filtered = useMemo(() => {
    const now2 = new Date();
    const base: Contact[] = isSearching ? serverContacts : contacts;
    return base.filter((c) => {
      if (typeFilter === 'this_month') {
        if (!c.createdAt || c.createdAt < 1000) return false;
        const d = new Date(c.createdAt);
        if (d.getMonth() !== now2.getMonth() || d.getFullYear() !== now2.getFullYear()) return false;
      } else if (typeFilter !== 'all' && c.type !== typeFilter) {
        return false;
      }
      if (!q || isSearching) return true; // server already matched when searching
      // Idle-path client search — name, organisation, email, phone, type and created date.
      return (
        c.name.toLowerCase().includes(q) ||
        (c.org ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q) ||
        (c.phone ?? '').toLowerCase().includes(q) ||
        formatDate(c.createdAt ?? 0).toLowerCase().includes(q) ||
        c.type.toLowerCase().includes(q)
      );
    });
  }, [contacts, serverContacts, isSearching, q, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  useEffect(() => { setPage(1); }, [q, typeFilter, pageSize]);

  const handleDelete = (id: string) => {
    const cl = contacts.find((c) => c.id === id);
    deleteContact(id);
    toast(`${cl?.name ?? 'Client'} removed`);
  };

  const handleSaveEdit = (updated: Contact) => {
    updateContact(updated.id, updated);
    setEditClient(null);
    toast.success(`${updated.name} updated`);
  };

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.info('No clients to export');
      return;
    }
    exportToCsv(
      filtered.map((c) => ({
        name: c.name,
        type: KIND_LABEL[c.type] ?? c.type,
        organisation: c.org,
        email: c.email,
        phone: c.phone,
        created: formatDate(c.createdAt ?? 0),
      })),
      'clients-export',
      [
        { key: 'name', label: 'Name' },
        { key: 'type', label: 'Type' },
        { key: 'organisation', label: 'Organisation' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'created', label: 'Created' },
      ],
    );
    toast.success(`Exported ${filtered.length} clients`);
  };

  const layCt = contacts.filter((c) => c.type === 'lay').length;
  const defCt = contacts.filter((c) => c.type === 'defendant').length;

  const now = new Date();
  const activeThisMonth = contacts.filter((c) => {
    if (!c.createdAt || c.createdAt < 1000) return false;
    const d = new Date(c.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="My Clients"
        subtitle="Lay clients, instructing solicitors, experts and witnesses you work with"
        actions={
          <>
            <Button variant="outline" size="md" leftIcon={<Download className="h-4 w-4" />} onClick={handleExport}>Export</Button>
            {canMutate && (
              <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/barrister/clients/new')}>New client</Button>
            )}
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <button type="button" onClick={() => setTypeFilter('all')} className={`rounded-xl text-left transition ${typeFilter === 'all' ? 'ring-2 ring-brand-500' : 'hover:opacity-90'}`}>
          <StatCard label="Total clients" value={String(contacts.length)} />
        </button>
        <button type="button" onClick={() => setTypeFilter('lay')} className={`rounded-xl text-left transition ${typeFilter === 'lay' ? 'ring-2 ring-brand-500' : 'hover:opacity-90'}`}>
          <StatCard label="Lay clients" value={String(layCt)} />
        </button>
        <button type="button" onClick={() => setTypeFilter('defendant')} className={`rounded-xl text-left transition ${typeFilter === 'defendant' ? 'ring-2 ring-brand-500' : 'hover:opacity-90'}`}>
          <StatCard label="Defendant clients" value={String(defCt)} />
        </button>
        <button type="button" onClick={() => setTypeFilter('this_month')} className={`rounded-xl text-left transition ${typeFilter === 'this_month' ? 'ring-2 ring-brand-500' : 'hover:opacity-90'}`}>
          <StatCard label="Active this month" value={String(activeThisMonth)} change={{ value: '', trend: 'up' }} />
        </button>
      </div>

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-border/60 bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, organisation, date…"
              className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground/50 focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-0.5 text-xs sm:w-full xl:w-auto">
            {[
              { k: 'all', label: 'All' },
              { k: 'lay', label: 'Lay' },
              { k: 'defendant', label: 'Defendant' },
              { k: 'solicitor', label: 'Solicitors' },
              { k: 'expert', label: 'Experts' },
              { k: 'witness', label: 'Witnesses' },
              { k: 'company', label: 'Companies' },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTypeFilter(t.k)}
                className={`whitespace-nowrap rounded-sm px-3 py-1.5 font-medium transition sm:flex-1 sm:px-2 sm:text-center xl:flex-none xl:px-3 ${typeFilter === t.k ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Organisation</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Matters</th>
                <th>Last contact</th>
              <th className="w-10 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((c) => (
              <tr key={c.id} className="cursor-pointer" onClick={() => setEditClient(c)}>
                <td className="font-medium text-foreground">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[10px] font-bold text-brand-700 dark:text-brand-300">
                      {c.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                    </div>
                    {c.name}
                  </div>
                </td>
                <td><Badge>{KIND_LABEL[c.type] ?? c.type}</Badge></td>
                <td className="text-muted-foreground">{c.org}</td>
                <td className="break-all text-muted-foreground">
                  {c.email || <span className="text-muted-foreground/40">—</span>}
                </td>
                <td className="break-all text-xs text-muted-foreground">
                  {c.phone || <span className="text-muted-foreground/40">—</span>}
                </td>
                <td className="text-xs text-muted-foreground">
                  <Briefcase className="mr-1 inline h-3 w-3" />{c.cases}
                </td>
                <td className="text-xs text-muted-foreground">{formatDate(c.createdAt)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-0.5">
                    <button
                      type="button"
                      title="Email"
                      aria-label="Email"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      onClick={() => { window.location.href = `mailto:${c.email}`; }}
                    >
                      <Mail className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Call"
                      aria-label="Call"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      onClick={() => { window.location.href = `tel:${c.phone}`; }}
                    >
                      <Phone className="h-4 w-4" />
                    </button>
                    <RowActions
                      onView={() => navigate(`/barrister/clients/${c.id}`)}
                      onEdit={canMutate ? () => setEditClient(c) : undefined}
                      onDelete={canDelete ? () => handleDelete(c.id) : undefined}
                      deleteConfirm={{
                        title: 'Delete contact',
                        message: `Delete contact "${c.name}"? This cannot be undone.`,
                        confirmLabel: 'Delete',
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                  {search ? 'No clients match your search.' : 'No clients yet. Click "New client" to add one.'}
                </td>
              </tr>
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <TablePagination
            page={safePage}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </Card>

      {/* Edit Client Modal */}
      {editClient && (
        <EditClientModal
          client={editClient}
          onSave={handleSaveEdit}
          onClose={() => setEditClient(null)}
        />
      )}
    </div>
  );
}

/* ─── Edit Client Modal ─── */

function EditClientModal({ client, onSave, onClose }: { client: Contact; onSave: (c: Contact) => void; onClose: () => void }) {
  const [form, setForm] = useState<Contact>({ ...client });
  const set = (field: keyof Contact) => (value: string) => setForm((f) => ({ ...f, [field]: value }));

  // Inline validation — messages render below each field.
  //
  // Format rules apply only to a field the user has CHANGED. A value already stored may
  // predate the rule that now judges it (the per-country phone lengths added 2026-10-06
  // invalidated some older numbers); flagging an untouched field would block every other
  // edit on the record, since `hasError` disables the save.
  const changed = (cur: string | null | undefined, orig: string | null | undefined) =>
    (cur ?? '') !== (orig ?? '');
  const nameError  = !form.name.trim()
    ? 'Name is required.'
    : (changed(form.name, client.name) && !isValidName(form.name) ? NAME_ERROR : '');
  const emailError = changed(form.email, client.email) && form.email?.trim() && !isValidEmail(form.email) ? EMAIL_ERROR : '';
  const phoneError = changed(form.phone, client.phone) && form.phone?.trim() && !isValidPhone(form.phone) ? PHONE_ERROR : '';
  const altPhoneError = changed(form.mobilePhone, client.mobilePhone) && form.mobilePhone?.trim() && !isValidPhone(form.mobilePhone) ? PHONE_ERROR : '';
  const hasError = !!(nameError || emailError || phoneError || altPhoneError);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16 backdrop-blur-sm" onClick={onClose}>
      <ScrollLock />
      <Card className="w-full max-w-lg shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-sm font-bold text-brand-700 dark:text-brand-300">
              {form.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}
            </div>
            <div>
              <div className="text-base font-semibold text-foreground">Edit Client</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{KIND_LABEL[form.type] ?? form.type}</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <CardContent className="space-y-4 p-6">
          {/* Type */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</label>
            <div className="flex gap-2">
              {TYPE_OPTIONS.map((k) => (
                <button key={k} onClick={() => setForm({ ...form, type: k })} className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${form.type === k ? 'border-brand-600 bg-brand-50 text-brand-700 dark:text-brand-300' : 'border-border hover:bg-muted'}`}>
                  {KIND_LABEL[k]}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Full name *</label>
            <input value={form.name} onChange={(e) => set('name')(sanitizeName(e.target.value))} className="h-9 w-full rounded-lg border border-border/70 bg-card px-3 text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
            {nameError && <p className="mt-1 text-xs text-danger">{nameError}</p>}
          </div>

          {/* Organisation */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organisation / Firm</label>
            <input value={form.org} onChange={(e) => set('org')(e.target.value)} placeholder="e.g. Magic Circle LLP" className="h-9 w-full rounded-lg border border-border/70 bg-card px-3 text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
          </div>

          {/* Email + Phone */}
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
              <input type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} className="h-10 w-full rounded-lg border border-border/70 bg-card px-3 text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
              {emailError && <p className="mt-1 text-xs text-danger">{emailError}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone</label>
              <PhoneInput value={form.phone} onChange={set('phone')} error={phoneError || undefined} className="w-full" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Alternate number</label>
              <PhoneInput value={form.mobilePhone ?? ''} onChange={set('mobilePhone')} error={altPhoneError || undefined} className="w-full" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</label>
            <textarea value={form.notes} onChange={(e) => set('notes')(e.target.value)} rows={3} placeholder="Any notes about this client…" className="w-full rounded-lg border border-border/70 bg-card px-3 py-2 text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
          </div>
        </CardContent>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="md" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" leftIcon={<CheckCircle className="h-4 w-4" />} disabled={hasError} onClick={() => onSave(form)}>
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}
